import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SectionLabel } from "./GradientText";

interface Logo {
  id: string;
  name: string;
  image: string;
}

interface IntegrationLogosProps {
  logos: Logo[];
}

/**
 * IntegrationLogos
 *
 * Horizontal logo bar showing integration partners.
 * Logos display in grayscale with color on hover.
 * Title from translations.
 */
export function IntegrationLogos({ logos }: IntegrationLogosProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("integrationLogos.title");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        {title && (
          <div
            className={cn(
              "mb-12 text-center lg:mb-16",
              "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
            )}
          >
            <SectionLabel>{title}</SectionLabel>
          </div>
        )}

        {/* Logo grid */}
        <div
          className={cn(
            "flex flex-wrap items-center justify-center gap-8 lg:gap-12",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          {logos.map((logo, index) => (
            <div
              key={logo.id}
              className={cn(
                "group relative",
                "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
              )}
              style={{
                transitionDelay: isVisible ? `${200 + index * 80}ms` : "0ms",
              }}
            >
              <div
                className={cn(
                  "flex h-12 w-24 items-center justify-center lg:h-14 lg:w-28",
                  "opacity-50 grayscale",
                  "hover:opacity-100 hover:grayscale-0",
                  "transition-all duration-300",
                )}
              >
                <img
                  src={logo.image}
                  alt={logo.name}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    // Fallback text for missing images
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement!.innerHTML = `
                      <span class="text-muted-foreground text-sm font-medium">${logo.name}</span>
                    `;
                  }}
                />
              </div>

              {/* Tooltip */}
              <div
                className={cn(
                  "absolute -bottom-8 left-1/2 -translate-x-1/2",
                  "rounded border border-border bg-popover px-2 py-1",
                  "text-xs whitespace-nowrap text-foreground",
                  "opacity-0 group-hover:opacity-100",
                  "transition-opacity duration-200",
                  "pointer-events-none",
                )}
              >
                {logo.name}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
